import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Table from '../../components/Table/Table';
import CrewAndCarsDocumentForm from '../../components/HR/CrewAndCarsDocumentForm';
import { FilterBar, FilterConfig, Pagination } from '../../components/Common';
import { CrewCertificate } from '../../types';
import { useCreateCrewCertificate, useUpdateCrewCertificate, useDeleteCrewCertificate } from '../../hooks/useCrewCertificates';
import { useEmployees } from '../../hooks/useEmployees';
import { useRanks } from '../../hooks/useRanks';
import { useDocumentTypes } from '../../hooks/useDocumentTypes';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { employeeDocumentsService, crewCertificatesService } from '../../services';
import DocumentDetailsScreen, { DocumentItem } from './DocumentDetailsScreen';
import ExpiryStatusBadges from './ExpiryStatusBadges';
import CarDocumentsSection from './CarDocumentsSection';
import { findEmployee, getCertificateExpiryStatus, handleExportDocuments, handlePrintDocuments, resolveDocumentExpiry } from './documentHelpers';
import { uploadFiles } from '../../services/upload.service';
import { carDocumentsService } from '../../services';
import { useCrewDocumentsPage, CrewDocumentFilters } from './useCrewDocumentsPage';

const STATUS_STYLES: Record<string, string> = {
  valid: 'bg-green-100 text-green-800',
  expiring_soon: 'bg-yellow-100 text-yellow-800',
  expired: 'bg-red-100 text-red-800',
};

const STATUS_LABELS: Record<string, string> = {
  valid: 'Valid',
  expiring_soon: 'Expiring Soon',
  expired: 'Expired',
};

const CrewAndCarsDocuments: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'crew' | 'cars'>('crew');
  const [showForm, setShowForm] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState<CrewCertificate | undefined>();
  const [viewingDocuments, setViewingDocuments] = useState<CrewCertificate | null>(null);
  const [showDocumentScreen, setShowDocumentScreen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [courseTypeFilter, setCourseTypeFilter] = useState<string>('all');
  const [professionFilter, setProfessionFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [rankFilter, setRankFilter] = useState<string>('all');
  const [expiryFilter, setExpiryFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');

  const debouncedSearch = useDebouncedValue(searchTerm);

  const documentFilters = useMemo<CrewDocumentFilters>(() => ({
    search: debouncedSearch,
    courseType: courseTypeFilter,
    profession: professionFilter,
    status: statusFilter,
    rank: rankFilter,
    expiry: expiryFilter,
    dateFrom: dateFromFilter,
    dateTo: dateToFilter,
  }), [debouncedSearch, courseTypeFilter, professionFilter, statusFilter, rankFilter, expiryFilter, dateFromFilter, dateToFilter]);

  const filtersKey = JSON.stringify(documentFilters);
  useEffect(() => { setPage(1); }, [filtersKey]);

  // Server-paginated merged list: uploaded documents first, then certificates
  const {
    rows, total, expiredCount, expiringSoonCount,
    isLoading, hasLoaded, error, refetch, fetchAllFiltered,
  } = useCrewDocumentsPage(page, pageSize, documentFilters);

  const employeeLookupOptions = useMemo(() => ({
    sort: [{ field: 'name', order: 'asc' as const }],
    pagination: { pageSize: 100 },
  }), []);
  const { data: employees = [] } = useEmployees(employeeLookupOptions);

  // Filter dropdowns read from the full reference lists (Document Settings,
  // Ranks), not from the current page of rows — otherwise only the ~50 loaded
  // documents' values appear as options.
  const { data: ranks = [] } = useRanks({ pagination: { pageSize: 500 } });
  const { data: documentTypes = [] } = useDocumentTypes({ pagination: { pageSize: 500 } });
  const rankNames = useMemo(
    () => ranks.map((r: any) => r.name || r.title).filter(Boolean) as string[],
    [ranks],
  );

  const createMutation = useCreateCrewCertificate(() => refetch());
  const updateMutation = useUpdateCrewCertificate(() => refetch());
  const deleteMutation = useDeleteCrewCertificate(() => refetch());

  const getEmployeeName = useCallback((id: string) => findEmployee(employees, id)?.name || '-', [employees]);
  const getEmployeeCode = useCallback((id: string) => findEmployee(employees, id)?.code || '-', [employees]);
  const getEmployeeRank = useCallback((id: string) => findEmployee(employees, id)?.rank || '-', [employees]);

  // Documents shown on the details screen: the employee's certificates plus
  // any uploaded employee documents (IDs, passports...) with their files.
  const [detailsDocuments, setDetailsDocuments] = useState<DocumentItem[]>([]);
  useEffect(() => {
    if (!viewingDocuments) {
      setDetailsDocuments([]);
      return;
    }
    let active = true;
    const employee = findEmployee(employees, viewingDocuments.employeeId);
    if (!employee?.documentId) return;
    const employeeFilter = { filters: { employee: { documentId: { $eq: employee.documentId } } } };
    Promise.all([
      crewCertificatesService.getAll(employeeFilter),
      employeeDocumentsService.getAll(employeeFilter),
    ])
      .then(([certsRes, docsRes]: any[]) => {
        if (!active) return;
        const certDocs: DocumentItem[] = (certsRes.data || []).map((c: any) => ({
          documentType: c.courseType,
          documentNumber: c.certificateNumber,
          issueDate: c.issueDate,
          expiryDate: resolveDocumentExpiry(c.courseType, c, c.employee),
          status: getCertificateExpiryStatus({ ...c, expiryDate: resolveDocumentExpiry(c.courseType, c, c.employee) }),
        }));
        const uploaded: DocumentItem[] = (docsRes.data || []).map((d: any) => ({
          documentType: d.documentType,
          documentNumber: d.documentNumber,
          issueDate: d.issueDate,
          expiryDate: resolveDocumentExpiry(d.documentType, d, d.employee),
          issuingAuthority: d.issuingAuthority,
          status: d.status || 'valid',
          filePath: d.filePath,
        }));
        setDetailsDocuments([...uploaded, ...certDocs]);
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, [viewingDocuments, employees]);

  const uniqueCourseTypes = Array.from(new Set([
    ...documentTypes.filter(dt => dt.category !== 'car').map(dt => dt.name).filter(Boolean),
    ...rows.map(c => c.courseType).filter(Boolean),
  ])).sort();
  const uniqueProfessions = Array.from(new Set([
    ...rankNames,
    ...rows.map(c => c.profession).filter((p): p is string => !!p),
  ])).sort();
  const uniqueRanks = rankNames.length
    ? rankNames
    : Array.from(new Set(employees.map(e => e.rank).filter(Boolean)));

  if (!hasLoaded && isLoading) {
    return <div className="flex justify-center items-center h-64"><div className="text-gray-600">Loading documents...</div></div>;
  }
  if (error) {
    return <div className="flex justify-center items-center h-64"><div className="text-red-600">Error loading documents. Please try again.</div></div>;
  }

  const handleExport = async () => {
    const all = await fetchAllFiltered();
    handleExportDocuments(all, getEmployeeCode, getEmployeeName, getEmployeeRank);
  };
  const handlePrint = async () => {
    const all = await fetchAllFiltered();
    handlePrintDocuments(all, employees, getEmployeeName);
  };

  const handleEdit = (cert: CrewCertificate) => {
    if ((cert as any).isUploadedDocument) {
      window.alert('هذا مستند مرفوع من ملف الموظف — يمكن تعديله من صفحة Employees بزر تعديل الموظف');
      return;
    }
    setEditingCertificate(cert); setShowForm(true);
  };
  const handleAdd = () => { setEditingCertificate(undefined); setShowForm(true); };
  const handleFormCancel = () => { setShowForm(false); setEditingCertificate(undefined); };

  const handleDelete = async (cert: CrewCertificate) => {
    if (!window.confirm(`Are you sure you want to delete document ${cert.certificateNumber || cert.courseType}?`)) return;
    if ((cert as any).isUploadedDocument) {
      await employeeDocumentsService.delete((cert as any).documentId);
      refetch();
    } else {
      await deleteMutation.execute(cert.documentId || cert.id);
    }
  };

  const handleFormSubmit = async (data: Partial<CrewCertificate>) => {
    if (editingCertificate) {
      await updateMutation.execute({
        id: editingCertificate.documentId || editingCertificate.id,
        data: { ...data, documentId: editingCertificate.documentId }
      });
    } else {
      const anyData = data as any;
      const file = (anyData.certificateFile as FileList | undefined)?.[0];
      const filePath = file ? (await uploadFiles([file]))[0]?.url : undefined;
      if (anyData.documentCategory === 'car') {
        // Car documents live in the car-document collection — a
        // crew-certificate record has no car field and would be orphaned.
        await carDocumentsService.create({
          carId: anyData.carId || null,
          documentType: anyData.courseType,
          documentNumber: anyData.certificateNumber || null,
          issueDate: anyData.issueDate || null,
          expiryDate: anyData.expiryDate || null,
          status: anyData.status || 'valid',
          filePath: filePath || null,
        });
        refetch();
      } else if (filePath) {
        // Uploaded employee papers (IDs, passports...) are stored as
        // employee-documents so the file stays attached to the record.
        await employeeDocumentsService.create({
          employeeId: anyData.employeeId || null,
          documentType: anyData.courseType,
          documentNumber: anyData.certificateNumber || null,
          issueDate: anyData.issueDate || null,
          expiryDate: anyData.expiryDate || null,
          status: anyData.status || 'valid',
          filePath,
        });
        refetch();
      } else {
        await createMutation.execute(data);
      }
    }
    setShowForm(false);
    setEditingCertificate(undefined);
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <CrewAndCarsDocumentForm certificate={editingCertificate} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
      </div>
    );
  }

  if (showDocumentScreen && viewingDocuments) {
    return (
      <DocumentDetailsScreen
        employeeName={getEmployeeName(viewingDocuments.employeeId!)}
        employeeCode={getEmployeeCode(viewingDocuments.employeeId!)}
        employeeRank={getEmployeeRank(viewingDocuments.employeeId!)}
        documents={detailsDocuments}
        onBack={() => { setViewingDocuments(null); setShowDocumentScreen(false); }}
      />
    );
  }

  const columns = [
    { key: 'employeeCode', header: 'Employee Code', label: 'Employee Code', sortable: true, render: (_: string, item: CrewCertificate) => getEmployeeCode(item.employeeId!) },
    { key: 'employeeName', header: 'Employee Name', label: 'Employee Name', sortable: true, render: (_: string, item: CrewCertificate) => getEmployeeName(item.employeeId!) },
    { key: 'rank', header: 'Rank', label: 'Rank', sortable: true, render: (_: string, item: CrewCertificate) => getEmployeeRank(item.employeeId!) },
    { key: 'courseType', header: 'Document Type', label: 'Document Type', sortable: true },
    { key: 'expiryDate', header: 'Expiry Date', label: 'Expiry Date', sortable: true,
      render: (value: string) => value ? new Date(value).toLocaleDateString() : '-' },
    { key: 'validityStatus', header: 'Document Status', label: 'Document Status', sortable: false,
      render: (_: any, item: CrewCertificate) => {
        const status = getCertificateExpiryStatus(item);
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status]}`}>
            {STATUS_LABELS[status]}
          </span>
        );
      } },
    { key: 'documents', header: 'Document Summary', label: 'Document Summary', sortable: false,
      render: (_: any, item: CrewCertificate) => (
        <button onClick={() => { setViewingDocuments(item); setShowDocumentScreen(true); }}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          View All Documents
        </button>
      ) }
  ];

  const filters: FilterConfig[] = [
    { key: 'courseType', label: 'Document Type', type: 'select', value: courseTypeFilter, onChange: setCourseTypeFilter,
      options: [{ value: 'all', label: 'All Document Types' }, ...uniqueCourseTypes.map(ct => ({ value: ct, label: ct }))] },
    { key: 'profession', label: 'Profession', type: 'select', value: professionFilter, onChange: setProfessionFilter,
      options: [{ value: 'all', label: 'All Professions' }, ...uniqueProfessions.map(p => ({ value: p, label: p }))] },
    { key: 'rank', label: 'Rank', type: 'select', value: rankFilter, onChange: setRankFilter,
      options: [{ value: 'all', label: 'All Ranks' }, ...uniqueRanks.map(r => ({ value: r, label: r }))] },
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter,
      options: [{ value: 'all', label: 'All Statuses' }, { value: 'valid', label: 'Valid' }, { value: 'expiring_soon', label: 'Expiring Soon' }, { value: 'expired', label: 'Expired' }] },
    { key: 'expiry', label: 'Expiry Status', type: 'select', value: expiryFilter, onChange: setExpiryFilter,
      options: [{ value: 'all', label: 'All' }, { value: 'valid', label: 'Valid' }, { value: 'expiring_soon', label: 'Expiring Soon' }, { value: 'expired', label: 'Expired' }] },
    { key: 'dateFrom', label: 'Issue Date From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Issue Date To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
  ];

  const clearFilters = () => {
    setSearchTerm(''); setCourseTypeFilter('all'); setProfessionFilter('all');
    setStatusFilter('all'); setRankFilter('all'); setExpiryFilter('all');
    setDateFromFilter(''); setDateToFilter('');
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Crew and Cars Documents Management</h1>
          <p className="text-lg text-gray-600 mt-2">Manage crew and cars documents, training records, and compliance</p>
        </div>
        {activeTab === 'crew' && (
          <ExpiryStatusBadges totalDocuments={total} expiringSoonCount={expiringSoonCount} expiredCount={expiredCount} />
        )}
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('crew')}
            className={`${
              activeTab === 'crew'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
          >
            Crew Documents
          </button>
          <button
            onClick={() => setActiveTab('cars')}
            className={`${
              activeTab === 'cars'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
          >
            Car Documents
          </button>
        </nav>
      </div>

      {activeTab === 'crew' && (
        <>
          <FilterBar
            searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search documents..."
            filters={filters} onClearFilters={clearFilters}
            totalCount={total} filteredCount={total}
            entityName="Crew and Cars Documents" onAdd={handleAdd} addLabel="Add Document"
            onExport={handleExport} onPrint={handlePrint}
          />

          <div className="card">
            <div className={isLoading ? 'opacity-50 pointer-events-none' : ''}>
              <Table columns={columns} data={rows} onEdit={handleEdit} onDelete={handleDelete}
                title="Crew Documents" showAddButton={false} searchable={false} />
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
              isLoading={isLoading}
            />
          </div>
        </>
      )}

      {activeTab === 'cars' && <CarDocumentsSection />}
    </div>
  );
};

export default CrewAndCarsDocuments;
